"use client";

import Link from "next/link";
import { useEffect } from "react";
import { withPostHog, loadObservability } from "@/shared/lib/observability/sinks";

/**
 * Route-level error boundary. Catches errors thrown anywhere in the app tree
 * BELOW the root layout — page render failures, hook throws, async errors
 * that bubble up. Sits between page components and `app/global-error.tsx`
 * (which only fires when the root layout itself crashes).
 *
 * The user keeps the chrome (nav, footer, providers) and sees a recoverable
 * error UI rather than a blank page. PostHog captures the error with the
 * active session/user context for debugging via Replays.
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // posthog-js loads lazily after first paint. A page that crashes on boot is
    // precisely the case we can't afford to drop, so pull the SDK in now rather than
    // waiting for idle; withPostHog queues this and replays it once it lands.
    void loadObservability();
    withPostHog((posthog) =>
      posthog.capture("$exception", {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_source: "route-error",
        $exception_stack_trace_raw: error.stack,
        digest: error.digest,
        path: window.location.pathname,
      })
    );
  }, [error]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-4 text-5xl">😵</div>
      <h2 className="mb-2 text-2xl font-medium text-slate-900">
        Something went wrong on this page
      </h2>
      <p className="mb-6 max-w-md text-sm text-slate-600">
        We&apos;ve been notified and are looking into it. You can try this page again
        or head back to the dashboard.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          onClick={reset}
          className="rounded-full bg-slate-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-slate-700"
        >
          Try again
        </button>
        <Link
          href="/user/dashboard"
          className="rounded-full border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-slate-50"
        >
          Go to dashboard
        </Link>
      </div>
      {error.digest && (
        <p className="mt-6 text-xs text-slate-400">
          Error ref: <code className="font-mono">{error.digest}</code>
        </p>
      )}
    </div>
  );
}
